import type { ReactNode } from 'react';
import Image from 'next/image';
import { SavvyIntelligenceMark } from '@savvy-kit/foundations/savvy-intelligence-mark';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { AlertCircle } from 'lucide-react';
import { SI_ALERT_TYPES } from 'src/constants/alerts';
import { CLIENT_SERVICES_PROFILE_PIC_URL } from 'src/utils/salesforce/account';

export type ActorKind = 'advisor' | 'client' | 'savvy_cs' | 'system';

type AvatarFrameProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly ariaLabel?: string;
};

type NotificationActorDisplayProps = {
  readonly actorKind: ActorKind;
  readonly actorName: string | null;
  readonly actorPhotoUrl: string | null;
  readonly typeOf: string;
};

function initials(name: string) {
  return name
    .split(' ')
    .filter((part) => part.length > 0)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function AvatarFrame({ children, className, ariaLabel }: AvatarFrameProps) {
  return (
    <span
      aria-label={ariaLabel}
      role={ariaLabel === undefined ? undefined : 'img'}
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-secondary',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function normalizeOptionalText(value: string | null) {
  const trimmed = value?.trim() ?? null;
  return trimmed !== null && trimmed.length > 0 ? trimmed : null;
}

export function NotificationActorDisplay({
  actorKind,
  actorName,
  actorPhotoUrl,
  typeOf,
}: NotificationActorDisplayProps) {
  const normalizedActorName = normalizeOptionalText(actorName);
  const normalizedActorPhotoUrl = normalizeOptionalText(actorPhotoUrl);

  if (SI_ALERT_TYPES.has(typeOf)) {
    return (
      <AvatarFrame className="bg-primary-foreground-light text-primary">
        <SavvyIntelligenceMark size="sm" className="[&_[data-slot=savvy-intelligence-mark-svg]]:size-6" />
      </AvatarFrame>
    );
  }

  if (actorKind === 'system') {
    return (
      <AvatarFrame ariaLabel="Needs your attention">
        <AlertCircle aria-hidden className="size-6" />
      </AvatarFrame>
    );
  }

  if (actorKind === 'savvy_cs') {
    // Reuses the same asset the task assignee dropdown shows for the
    // Savvy Client Services account (src/utils/tasks.ts → assigneeAvatarUrl) so
    // the brand mark is consistent across the app. The notifications anatomy
    // spec calls for the Savvy logomark on green — not the AI sparkle.
    return (
      <Image
        src={CLIENT_SERVICES_PROFILE_PIC_URL}
        alt={normalizedActorName ?? 'Savvy Client Services'}
        width={32}
        height={32}
        unoptimized
        className="size-8 shrink-0 rounded-full object-cover"
      />
    );
  }

  if (normalizedActorPhotoUrl !== null) {
    return (
      <Image
        src={normalizedActorPhotoUrl}
        alt={normalizedActorName ?? 'Unknown actor'}
        width={32}
        height={32}
        unoptimized
        className="size-8 shrink-0 rounded-full object-cover"
      />
    );
  }

  const fallback = normalizedActorName === null ? '?' : initials(normalizedActorName);
  return (
    <AvatarFrame ariaLabel={normalizedActorName ?? 'Unknown actor'}>
      <Text className="text-xs font-medium text-secondary">{fallback}</Text>
    </AvatarFrame>
  );
}
